import React from 'react';
import cn from 'classnames';
import styles from './Boxes.module.scss';
import { charCodes } from '../constants';

const constants = {
  spacingXs: '4px',
  spacingSm: '8px',
  spacingMd: '16px',
  spacingLg: '24px',
  spacingBg: '32px',
  spacingXl: '48px',
  spacingXxl: '64px',
  spacingXxxl: '128px'
};

const getSpacing = (spacing) => {
  if (spacing === 'xs') return constants.spacingXs;
  if (spacing === 'md') return constants.spacingMd;
  if (spacing === 'lg') return constants.spacingLg;
  if (spacing === 'sm') return constants.spacingSm;
  if (spacing === 'xl') return constants.spacingXl;
  if (spacing === 'xxl') return constants.spacingXxl;
  if (spacing === 'xxxl') return constants.spacingXxxl;
  return null;
};

export const FlexBox = (props) => {
  const style = {
    flex: props.flex,
    alignItems: props.alignItems,
    justifyContent: props.justifyContent,
    flexDirection: props.flexDirection,
    flexWrap: props.flexWrap,
    flexGrow: props.flexGrow,
    flexShrink: props.flexShrink,
    marginTop: getSpacing(props.marginTop),
    marginLeft: getSpacing(props.marginLeft),
    marginRight: getSpacing(props.marginRight),
    marginBottom: getSpacing(props.marginBottom),
    paddingTop: getSpacing(props.paddingTop),
    paddingLeft: getSpacing(props.paddingLeft),
    paddingRight: getSpacing(props.paddingRight),
    paddingBottom: getSpacing(props.paddingBottom),
    ...props.style,
  };
  const { onClick } = props;
  if (props.margin) style.margin = getSpacing(props.margin);
  if (props.marginVertical) {
    style.marginTop = getSpacing(props.marginVertical);
    style.marginBottom = getSpacing(props.marginVertical);
  }
  if (props.padding) style.padding = getSpacing(props.padding);
  return (
    <div className={cn(styles.flexBox, props.className)} onClick={onClick} style={style}>
      {props.children}
    </div>
  );
};

FlexBox.defaultProps = {
  style: {},
};

export const InlineFlexBox = (props) => (
  <FlexBox
    {...props}
    className={cn(styles.inlineFlexBox, props.className)}
    style={{
      flex: props.flex,
      alignItems: props.alignItems,
      justifyContent: props.justifyContent,
      flexDirection: props.flexDirection,
      flexWrap: props.flexWrap,
      ...props.style,
    }}>
    {props.children}
  </FlexBox>
);

InlineFlexBox.defaultProps = {
  style: {},
};

export const ColumnFlexBox = (props) => (
  <FlexBox flexDirection="column" {...props} />
);

export const CenteredBoxSmall = (props) => (
  <ColumnFlexBox className={styles.centeredBoxSmall}>
    {props.children}
  </ColumnFlexBox>
);

const mergeIfExists = (key, value) => {
  if (!value) return {};
  return { [key]: value };
};

export const SpacingBox = (props) => (
  <div
    className={props.className}
    style={{
      marginTop: getSpacing(props.marginTop),
      marginLeft: getSpacing(props.marginLeft),
      marginRight: getSpacing(props.marginRight),
      marginBottom: getSpacing(props.marginBottom),
      paddingTop: getSpacing(props.paddingTop),
      paddingLeft: getSpacing(props.paddingLeft),
      paddingRight: getSpacing(props.paddingRight),
      paddingBottom: getSpacing(props.paddingBottom),
      ...mergeIfExists('margin', getSpacing(props.margin)),
      ...mergeIfExists('padding', getSpacing(props.padding)),
      ...props.style,
    }}>
    {props.children}
  </div>
);

SpacingBox.defaultProps = {
  style: {},
};

export const DescriptionBox = ({ children }) => (
  <SpacingBox padding="md">{children}</SpacingBox>
);

export const ClickableBox = ({
  children,
  onClick,
  onMouseEnter,
  onMouseLeave,
  className,
  style,
}) => {
  const spaceHandler = (e) => {
    if (!e) return null;
    e.preventDefault();
    e.stopPropagation();
    if (e.charCode === charCodes.enter) {
      onClick(e);
    }
    return null;
  };

  return (
    <div
      onKeyPress={spaceHandler}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      role="button"
      tabIndex={0}
      style={style}
      className={cn(styles.clickableBox, className)}
      onClick={onClick}>
      {children}
    </div>
  );
};

export const ClickableBoxWithUnderline = ({ isUnderlined, ...props }) => (
  <ClickableBox
    className={isUnderlined ? styles.isUnderlined : styles.isNotUnderlined}
    {...props}
  />
);

export const ClickableFlexBox = ({ children, ...props }) => (
  <ClickableBox {...props}>
    <FlexBox alignItems="center">{children}</FlexBox>
  </ClickableBox>
);

export const RelativeBox = ({ children }) => (
  <div className={styles.relativeBox}>{children}</div>
);

export const InlineItemWithMargin = ({ children }) => (
  <SpacingBox marginLeft="lg">
    <div>{children}</div>
  </SpacingBox>
);

export const HorizontalScrollableBox = ({ children, maxWidth }) => (
  <div style={{ maxWidth }} className={styles.horizontalScrollableBox}>
    {children}
  </div>
);

export const SquareFlexBox = (props) => (
    <FlexBox
      {...props}
      style={{
        overflow: 'hidden',
        backgroundColor: props.backgroundColor,
        minWidth: props.size,
        minHeight: props.size,
        maxWidth: props.size,
        maxHeight: props.size,
        ...props.style,
      }}>
      {props.children}
    </FlexBox>
);

export const RowFlexBox = (props) => <FlexBox {...props} flexDirection="row" />;
